'use client';
import React from 'react';
import ArrowPathIcon from '@heroicons/react/16/solid/ArrowPathIcon';
import { ProductCardProduct } from 'shared/types/product/Product';
import { ProductPaginatedResult } from 'shared/types/result/PaginatedResult';
import ProductList from 'frontastic/tastics/products/product-list/components/ProductList';
import {
  ProductSearchProvider,
  useProductSearchContext,
} from 'frontastic/tastics/products/product-list/ProductSearchContext';

interface PLPPlaceholderProps {
  className?: string;
  searchContextValues?: any;
  categoryId?: string;
  title?: string;
  plpTitle?: string;
  defaultColor?: string;
}

const PLPPlaceholder: React.FC<PLPPlaceholderProps> = ({
  className,
  searchContextValues,
  categoryId,
  plpTitle,
  defaultColor,
}) => {
  // ==========================================================================================================================
  // THIS FALLBACK IN CASE THE SEARCH PROVIDER IS NOT AVAILABLE IS NEEDED FOR THE AMPLIENCE VISUALISATION FUNCTIONALITY TO WORK
  // ==========================================================================================================================
  if (!searchContextValues) {
    const VisualisationProductList = () => {
      const { productsPaginatedResults, isLoading, sort, setSort, offset, setOffset, activeFacets, setActiveFacets } =
        useProductSearchContext();

      const totalItems = productsPaginatedResults?.data?.total || 0;
      if (productsPaginatedResults?.data?.facets) {
        productsPaginatedResults.data.facets = [
          {
            name: 'Facets placeholder',
            buckets: [
              {
                key: 'Value 1',
                count: 596,
              },
              {
                key: 'Value 2',
                count: 223,
              },
            ],
            collapse: false,
          },
          {
            name: 'Facets placeholder 2',
            buckets: [
              {
                key: 'Value 3',
                count: 25,
              },
              {
                key: 'Value 4',
                count: 18,
              },
            ],
            collapse: false,
          },
          {
            name: 'Facets placeholder 3',
            buckets: [
              {
                key: 'Value 5',
                count: 25,
              },
              {
                key: 'Value 6',
                count: 18,
              },
            ],
            collapse: false,
          },
        ];
      }

      return (
        <>
          <div className="mt-8">
            <div className="flex flex-col items-center justify-between">
              <h1 id="title" className="pb-5 text-center text-2xl font-bold">
                PLP TITLE PLACEHOLDER
                {activeFacets.length === 1 &&
                  activeFacets[0].facetData?.terms?.length === 1 &&
                  ` | ${activeFacets[0].facetData?.terms?.[0]}`}
              </h1>
              {totalItems > 0 && (
                <p className="min-w-40 px-5 pb-8 pt-4 text-center text-sm font-normal">{totalItems} items</p>
              )}
            </div>
          </div>
          <div className={className}>
            <ProductList
              isLoading={isLoading}
              productsPaginatedResults={productsPaginatedResults as { data: ProductPaginatedResult }}
              activeFacets={activeFacets}
              setActiveFacets={setActiveFacets}
              offset={offset}
              setOffset={setOffset}
              sort={sort}
              setSort={setSort}
              isVisualisation={true}
              defaultColor={defaultColor}
            />
          </div>
        </>
      );
    };

    return (
      <ProductSearchProvider>
        <VisualisationProductList />
      </ProductSearchProvider>
    );
  }
  // ==========================================================================================================================

  const { productsPaginatedResults, isLoading, sort, setSort, offset, setOffset, activeFacets, setActiveFacets } =
    searchContextValues;
  const pinnedItemsCount =
    productsPaginatedResults?.data?.items?.filter((item: ProductCardProduct) => item.isPinned === true).length || 0;
  const totalItemsFromResults = productsPaginatedResults?.data?.total || 0;
  const totalItems = totalItemsFromResults + pinnedItemsCount;

  // Helper function to get display label for filter terms in title
  const getFilterDisplayLabel = () => {
    if (activeFacets.length === 1 && activeFacets[0].facetData?.terms?.length === 1) {
      const facet = activeFacets[0];
      const term = facet.facetData?.terms?.[0];

      // For Ready to Ship (isOnStock), show "Ready to Ship" instead of "true"
      if (facet.key === 'variants.availability.isOnStock' && term === 'true') {
        return ' | Ready to Ship';
      }

      return ` | ${term}`;
    }
    return '';
  };

  return (
    <>
      <div className="mt-8">
        <div className="flex flex-col items-center justify-between">
          <h1 id="title" className="pb-5 text-center text-2xl font-bold">
            {plpTitle}
            {getFilterDisplayLabel()}
          </h1>
          <div className="h-px min-w-40 bg-primary-gold" />
          {totalItems > 0 && (
            <p className="min-w-40 px-5 pb-8 pt-4 text-center text-sm font-normal">{totalItems} items</p>
          )}
        </div>
      </div>
      {productsPaginatedResults.data ? (
        <div className={className}>
          <ProductList
            isLoading={isLoading}
            productsPaginatedResults={productsPaginatedResults as { data: ProductPaginatedResult }}
            activeFacets={activeFacets}
            setActiveFacets={setActiveFacets}
            offset={offset}
            setOffset={setOffset}
            sort={sort}
            setSort={setSort}
            categoryId={categoryId}
            location="PLP Category"
            defaultColor={defaultColor}
          />
        </div>
      ) : (
        <div className="flex h-dvh w-full items-center justify-center gap-x-2">
          <ArrowPathIcon className="size-4 animate-spin fill-primary-gray stroke-1" />
          <p>Loading...</p>
        </div>
      )}
    </>
  );
};

export default PLPPlaceholder;
